import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SubmitButton } from '../../components/mui/Buttons/Buttons';
import Loader3D from '../../components/Loader/Loader3D';
import { getLeadsForDeo, postLeadForDeoSkillx } from '../../redux/features/deoSlice';
import { useSelector } from 'react-redux';
import { preferredTypes } from '../../components/datagrid/deo/SkillxAddorEditDeoLeads';
import { Icon } from '@iconify/react/dist/iconify.js';

const BulkUploadView = ({ dispatchParams,setAlertContent,setAlertOpen }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((store) => store.deo);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [excellConvertionLoading, setExcellConvertionLoading] = useState(false);
  const initialValues = {
    excelData: [],
  };

  const allowedKeys = [
    'lead_name',
    'lead_country_code',
    'lead_mob_no',
    'lead_whatsapp_no',
    'lead_email_id',
    'lead_place',
    'age',
    'gender',
    'lead_class_mode',
    'lead_remarks',
  ];

  const schema = Yup.object({
    excelData: Yup.array()
      .of(
        Yup.object()
          .shape({
            lead_name: Yup.string().trim().required('Lead name is required'),
            lead_country_code: Yup.number()
              .typeError('Country code must be a number')
              .required('Lead country code is required'),
            lead_mob_no: Yup.number()
              .typeError('Mobile number must be numeric')
              .required('Lead mobile number is required'),
            lead_whatsapp_no: Yup.number()
              .typeError('WhatsApp number must be numeric')
              .required('Lead WhatsApp number is required'),
            lead_email_id: Yup.string().email('Invalid email format').required('Lead email is required'),
            lead_place: Yup.string().required('Lead place is required'),
            age: Yup.number()
              .integer('Age must be a whole number')
              .min(1, 'Age must be positive')
              .required('Age is required'),
            gender: Yup.string().oneOf(['Male', 'Female'], 'Invalid gender').required('Gender is required'),
            lead_class_mode: Yup.string()
              .oneOf(['Online', 'Offline'], 'Invalid class mode')
              .required('Class mode is required'),
            lead_remarks: Yup.string().required('Remarks are required'),
          })
          .test('no-extra-keys', 'Invalid keys found in Excel', (value) => {
            if (!value) return false;
            const keys = Object.keys(value);
            const extraKeys = keys.filter((k) => !allowedKeys.includes(k));
            return extraKeys.length === 0;
          })
      )
      .min(1, 'Please upload at least one row in Excel')
      .required('Excel data is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const data = values.excelData.map((item) => ({
        ...item,
        lead_class_mode: preferredTypes.find((p) => p?.name === item.lead_class_mode)?.id || null,
      }));

      dispatch(postLeadForDeoSkillx({ data })).then((res) => {
        if (res.payload.status === 'error') {
          enqueueSnackbar(res.payload.message, {
            variant: 'error',
          });
        } else if (res.payload.status === 'success') {
          dispatch(getLeadsForDeo(dispatchParams));
          handleCancel();
          // enqueueSnackbar(res.payload.message, {
          //   variant: 'success',
          // });
          setAlertContent({
            title: 'Success',
            message: res.payload.message,
            data: res.payload.data,
          });
          setAlertOpen(true);
        }
      });
    },
  });
  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
  };

  const downloadSampleExcel = () => {
    const link = document.createElement('a');
    link.href = '/sheets/skillxdeoLeadsUploadStructure.xlsx';
    link.download = 'SkillX_Lead_Upload_Structure.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setExcellConvertionLoading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const binaryString = e.target.result;
          const workbook = XLSX.read(binaryString, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          formik.setFieldValue('excelData', json);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          formik.setFieldValue('excelData', []);
        } finally {
          setExcellConvertionLoading(false);
        }
      };
      reader.onerror = () => {
        console.error('File reading error');
        setExcellConvertionLoading(false);
      };
      reader.readAsBinaryString(file);
    }
  };


  return (
    <GlobalDialog
      maxWidth={'sm'}
      fullWidth
      title={'Action'}
      open={open}
      setOpen={setOpen}
      closeButton={true}
      closeCondition={handleCancel}
      popupButton={
        <>
          <AddButton size={'small'} action={() => setOpen(true)}>
            Bulk Upload
          </AddButton>
        </>
      }
      content={
        open ? (
          loading ? (
            <Loader3D />
          ) : (
            <>
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Box>
                    <Stack gap={2}>
                      <Box mb={1}>
                        <Button size="small" variant="outlined" onClick={downloadSampleExcel}>
                          <Icon icon="vscode-icons:file-type-excel2" width="25" height="25" />
                          <Typography ml={'4px'} variant={'button'}>
                            Download Excell Structure
                          </Typography>
                        </Button>
                      </Box>

                      <Box>
                        <TextField
                          name="excelData"
                          inputRef={fileInputRef}
                          InputLabelProps={{ shrink: true }}
                          placeholder="Select an Excel File to Upload"
                          label="Select an Excel File to Upload"
                          variant="outlined"
                          type="file"
                          onChange={handleFileChange}
                          accept=".xlsx, .xls"
                          error={formik.touched.excelData && Boolean(formik.errors.excelData)}
                          helperText={
                            formik.touched.excelData && formik.errors.excelData
                              ? typeof formik.errors.excelData === 'string'
                                ? formik.errors.excelData
                                : Array.isArray(formik.errors.excelData) &&
                                  typeof formik.errors.excelData[0] === 'string'
                                ? formik.errors.excelData[0]
                                : 'Invalid or missing data in Excel rows.'
                              : ''
                          }
                        />
                      </Box>
                      {formik.errors.excelData && Array.isArray(formik.errors.excelData) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {typeof formik.errors.excelData[0] === 'string'
                            ? formik.errors.excelData
                            : formik.errors.excelData
                                .map((err, i) => (err ? `Row ${i + 1}: ${Object.values(err).join(', ')}` : ''))
                                .join(' | ')}
                        </Alert>
                      )}
                    </Stack>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Stack direction={'row'} spacing={1}>
                      <CancelButton action={() => handleCancel()}>Cancel</CancelButton>

                      <SubmitButton disabled={loading || excellConvertionLoading} type="submit">
                        {loading ? 'Submiting...' : 'Submit'}
                      </SubmitButton>
                    </Stack>
                  </Box>
                </Form>
              </FormikProvider>
            </>
          )
        ) : null
      }
    />
  );
};

export default BulkUploadView;
